import { TripType, type CalendarBuckets } from "@b2bportal/air-shopping-api";
import type { FlightState } from "@b2bportal/core-flights";
import {
  type IMonthBucket,
  type ITripTerminus,
  type SerializableMonthBucket,
  TripFilter,
} from "@b2bportal/core-types";
import { serializableMonthBuckets } from "@b2bportal/core-utilities";
import { TripCategory } from "@overrides/types";
import { flightsShoppingApi } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import type { IBucketedDate, IDateBucket } from "../../../../types";

interface FetchCalendarAction {
  origin: ITripTerminus;
  destination: ITripTerminus;
  tripCategory: TripCategory;
  // For multicity flights, indicates which slice the calendar data is for
  sliceIndex?: number;
}
export const fetchCalendar = createAsyncThunk<
  {
    departureMonths: SerializableMonthBucket[];
    priceTags: string[];
    origin: ITripTerminus;
    destination: ITripTerminus;
    tripCategory: TripCategory;
    sliceIndex?: number;
  },
  FetchCalendarAction,
  { state: FlightState; rejectValue: { error: string } }
>(
  "flightsShop/fetchCalendar",
  async (action: FetchCalendarAction, thunkAPI) => {
    const state = thunkAPI.getState();

    const { origin, destination, tripCategory, sliceIndex } = action;
    const { pricePrediction, multicityPricePredictions } =
      state.flights.flightSearch;

    if (origin == null || destination == null) {
      return thunkAPI.rejectWithValue({
        error: "Origin or destination is not set",
      });
    }

    /** If the price prediction is already set, return it **/
    // For multicity flights, check the specific slice's cached data
    const existingPrediction =
      sliceIndex !== undefined && sliceIndex < multicityPricePredictions.length
        ? multicityPricePredictions[sliceIndex]
        : sliceIndex === undefined
          ? pricePrediction
          : undefined;

    if (
      existingPrediction != null &&
      existingPrediction?.priceTags?.length > 0 &&
      existingPrediction?.departureMonths?.length > 0 &&
      existingPrediction?.origin === origin &&
      existingPrediction?.destination === destination &&
      existingPrediction?.tripCategory === tripCategory
    ) {
      return {
        departureMonths: existingPrediction.departureMonths,
        priceTags: existingPrediction.priceTags,
        origin,
        destination,
        tripCategory,
        sliceIndex,
      };
    }

    const {
      id: { code: destinationCode },
    } = destination;
    const {
      id: { code: originCode },
    } = origin;

    const requestBody: CalendarBuckets = {
      route: { origin: originCode, destination: destinationCode },
      tripType:
        tripCategory === TripCategory.ROUND_TRIP
          ? TripType.round_trip
          : TripType.one_way,
      filter: { TripFilter: TripFilter.NO_FILTER },
    };

    try {
      const res = await flightsShoppingApi.apiV0CalendarPost(requestBody);

      const calendarReport = res.data;
      const departureDateBuckets =
        calendarReport.departureDateReport.departureDateBuckets;

      const departureMonths = serializableMonthBuckets(
        transformDateBuckets(departureDateBuckets)
      );

      const priceTags = departureDateBuckets.map(
        (dateBucket: IDateBucket) => dateBucket.legend
      );
      return {
        departureMonths,
        priceTags,
        origin,
        destination,
        tripCategory,
        sliceIndex,
      };
    } catch (e) {
      return thunkAPI.rejectWithValue({ error: String(e) });
    }
  }
);

export const transformDateBuckets = (
  dateBuckets: IDateBucket[]
): IMonthBucket[] => {
  const processedMonths = dateBuckets.reduce(
    (months, { dates }, bucketIndex) => {
      const reducedMonths = dates.reduce(
        (monthBuckets: IMonthBucket[], dateString: string) => {
          const date = dayjs(dateString).toDate();
          const bucketedDate: IBucketedDate = { bucket: bucketIndex, date };
          const currentMonthIndex = dayjs(date).month();
          let existingMonthBucket = monthBuckets.find(({ monthIndex }) => {
            return monthIndex === currentMonthIndex;
          });

          if (typeof existingMonthBucket === "undefined") {
            existingMonthBucket = { monthIndex: currentMonthIndex, dates: [] };
            monthBuckets.push(existingMonthBucket);
          }

          existingMonthBucket.dates.push(bucketedDate);

          return monthBuckets;
        },
        months
      );

      return reducedMonths;
    },
    [] as IMonthBucket[]
  );
  const sortedMonths = processedMonths.map((processedMonth) => {
    const sortedDates = processedMonth.dates.sort(
      (a: IBucketedDate, b: IBucketedDate) => dayjs(a.date).diff(b.date)
    );
    return { monthIndex: processedMonth.monthIndex, dates: sortedDates };
  });

  return sortedMonths;
};
